import React, { useState } from 'react';
import { auth } from '../firebaseConfig';
import { useProfile } from '../context/ProfileContext';
import { submitFeedback, MAX_FEEDBACK_MESSAGE_LENGTH } from '../services/feedbackService';
import IconButton from './IconButton';
import './comic.css';

// "Contact" speech-bubble feedback/help form. Extracted out of
// ProfileSwitcher.jsx (2026-08-13) so it can sit as its own button next to
// "Log Out" in App.js, rather than as a small text link under the Hero
// switcher. Also passes the account's real sign-up email (auth.currentUser,
// not anything Hero-scoped) alongside the message so sendContactEmail (the
// Cloud Function backing this) can set it as the outgoing email's Reply-To
// -- otherwise a reply in Gmail has nowhere real to go.
const Contact = () => {
  const { activeProfile } = useProfile();

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);

  const closeFeedback = () => {
    setFeedbackOpen(false);
    setFeedbackMessage("");
    setFeedbackSubmitting(false);
    setFeedbackSent(false);
    setFeedbackError(null);
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    const trimmed = feedbackMessage.trim();
    if (!trimmed || feedbackSubmitting) return;

    setFeedbackSubmitting(true);
    setFeedbackError(null);
    const result = await submitFeedback(
      activeProfile?.ownerUserId,
      activeProfile?.id,
      activeProfile?.name,
      trimmed,
      auth.currentUser?.email
    );
    setFeedbackSubmitting(false);

    if (result.success) {
      setFeedbackSent(true);
      setFeedbackMessage("");
    } else {
      setFeedbackError("Couldn't send that. Try again.");
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => (feedbackOpen ? closeFeedback() : setFeedbackOpen(true))}
        aria-expanded={feedbackOpen}
        data-tour="contact-button"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          flex: 1,
          height: "48px",
          backgroundColor: "#00a274",
          color: "#fff",
          fontSize: "16px",
          borderRadius: "8px",
          cursor: "pointer",
          border: "none"
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
        Contact
      </button>

      {feedbackOpen && (
        <div className="comic-speech-row" style={{ marginTop: "8px", flexBasis: "100%" }}>
          <div className="comic-avatar">
            {activeProfile?.photoURL ? (
              <img src={activeProfile.photoURL} alt="" className="comic-avatar__img" />
            ) : (
              <svg viewBox="0 0 24 24" className="comic-avatar__icon" aria-hidden="true">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
              </svg>
            )}
          </div>
          <div className="comic-speech-bubble">
            <div style={{ position: "absolute", top: "-10px", right: "-10px" }}>
              <IconButton icon="close" onClick={closeFeedback} color="#232526" background="#ffffff" label="Close feedback" size={28} />
            </div>
            {feedbackSent ? (
              "Thanks, Hero! We got your message."
            ) : (
              <form onSubmit={handleFeedbackSubmit}>
                Got feedback, or need a hand with something? Tell us below.
                <textarea
                  value={feedbackMessage}
                  onChange={(e) => setFeedbackMessage(e.target.value.slice(0, MAX_FEEDBACK_MESSAGE_LENGTH))}
                  placeholder="Type your message..."
                  rows={3}
                  className="comic-input"
                  style={{
                    display: "block",
                    width: "100%",
                    marginTop: "8px",
                    resize: "vertical",
                    fontFamily: "'Anonymous Pro', monospace"
                  }}
                  autoFocus
                />
                <button
                  type="submit"
                  disabled={!feedbackMessage.trim() || feedbackSubmitting}
                  className="comic-btn comic-btn--go"
                >
                  {feedbackSubmitting ? "Sending..." : "Send"}
                </button>
                {feedbackError && <div className="comic-alert-bubble">{feedbackError}</div>}
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Contact;
